import {
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';

export const SYSTEM_ASSIGNMENT_DEFAULTS = {
    aiGradingEnabled: true,
    gradingCustomContext: '',
    aiFeedbackEnabled: true,
    feedbackSettings: {
        structure: DEFAULT_FEEDBACK_STRUCTURE,
        numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
        suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
        languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
        tone: DEFAULT_FEEDBACK_TONE,
        customInstructions: '',
    },
    feedbackCustomContext: '',
    aiStudentFeedbackEnabled: false,
    studentFeedbackSettings: {
        structure: DEFAULT_FEEDBACK_STRUCTURE,
        numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
        suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
        languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
        tone: DEFAULT_FEEDBACK_TONE,
        customInstructions: '',
    },
    studentFeedbackCustomContext: '',
    aiInlineCommentsEnabled: true,
    inlineCommentsSettings: {
        maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
        commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
        languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
        tone: DEFAULT_INLINE_COMMENTS_TONE,
    },
    inlineCommentsCustomContext: '',
};

const merge = (base, value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return base;
    return Object.keys(base).reduce((result, key) => {
        const next = value[key];
        result[key] =
            next && typeof next === 'object' && !Array.isArray(next)
                ? merge(base[key], next)
                : next === undefined
                  ? base[key]
                  : next;
        return result;
    }, {});
};

export const mergeAssignmentDefaults = (defaults) => merge(SYSTEM_ASSIGNMENT_DEFAULTS, defaults);

export const assignmentDefaultsToApi = (form) => ({
    ai_grading_enabled: Boolean(form.aiGradingEnabled),
    ai_grading_context: form.gradingCustomContext || '',
    ai_feedback_enabled: Boolean(form.aiFeedbackEnabled),
    ai_feedback_settings: {
        structure: form.feedbackSettings.structure,
        num_suggestions: form.feedbackSettings.numSuggestions,
        suggestion_length: form.feedbackSettings.suggestionLength,
        language_level: form.feedbackSettings.languageLevel,
        tone: form.feedbackSettings.tone,
        custom_instructions: form.feedbackSettings.customInstructions || '',
    },
    ai_feedback_context: form.feedbackCustomContext || '',
    ai_student_feedback_enabled: Boolean(form.aiStudentFeedbackEnabled),
    ai_student_feedback_settings: {
        structure: form.studentFeedbackSettings.structure,
        num_suggestions: form.studentFeedbackSettings.numSuggestions,
        suggestion_length: form.studentFeedbackSettings.suggestionLength,
        language_level: form.studentFeedbackSettings.languageLevel,
        tone: form.studentFeedbackSettings.tone,
        custom_instructions: form.studentFeedbackSettings.customInstructions || '',
    },
    ai_student_feedback_context: form.studentFeedbackCustomContext || '',
    ai_inline_comments_enabled: Boolean(form.aiInlineCommentsEnabled),
    ai_inline_comments_settings: {
        max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
        comment_length: form.inlineCommentsSettings.commentLength,
        language_level: form.inlineCommentsSettings.languageLevel,
        tone: form.inlineCommentsSettings.tone,
    },
    ai_inline_comments_context: form.inlineCommentsCustomContext || '',
});

export const assignmentDefaultsFromApi = (defaults) => {
    const value = mergeAssignmentDefaults({
        aiGradingEnabled: defaults?.ai_grading_enabled,
        gradingCustomContext: defaults?.ai_grading_context,
        aiFeedbackEnabled: defaults?.ai_feedback_enabled,
        feedbackSettings: {
            structure: defaults?.ai_feedback_settings?.structure,
            numSuggestions: defaults?.ai_feedback_settings?.num_suggestions,
            suggestionLength: defaults?.ai_feedback_settings?.suggestion_length,
            languageLevel: defaults?.ai_feedback_settings?.language_level,
            tone: defaults?.ai_feedback_settings?.tone,
            customInstructions: defaults?.ai_feedback_settings?.custom_instructions,
        },
        feedbackCustomContext: defaults?.ai_feedback_context,
        aiStudentFeedbackEnabled: defaults?.ai_student_feedback_enabled,
        studentFeedbackSettings: {
            structure: defaults?.ai_student_feedback_settings?.structure,
            numSuggestions: defaults?.ai_student_feedback_settings?.num_suggestions,
            suggestionLength: defaults?.ai_student_feedback_settings?.suggestion_length,
            languageLevel: defaults?.ai_student_feedback_settings?.language_level,
            tone: defaults?.ai_student_feedback_settings?.tone,
            customInstructions: defaults?.ai_student_feedback_settings?.custom_instructions,
        },
        studentFeedbackCustomContext: defaults?.ai_student_feedback_context,
        aiInlineCommentsEnabled: defaults?.ai_inline_comments_enabled,
        inlineCommentsSettings: {
            maxCommentsPerPage: defaults?.ai_inline_comments_settings?.max_comments_per_page,
            commentLength: defaults?.ai_inline_comments_settings?.comment_length,
            languageLevel: defaults?.ai_inline_comments_settings?.language_level,
            tone: defaults?.ai_inline_comments_settings?.tone,
        },
        inlineCommentsCustomContext: defaults?.ai_inline_comments_context,
    });
    return value;
};
